<template>
    <canvas ref="canvas" height="200" style="width: 100%;" />
</template>

<script>
function timeToMinutes(str) {
    if (!str) return 0
    const lower = str.toLowerCase().trim()
    const isPm  = lower.includes('pm')
    const isAm  = lower.includes('am')
    const clean = lower.replace(/[apm\s]/g, '')
    let [h, m]  = clean.split(':').map(Number)
    if (isPm && h !== 12) h += 12
    if (isAm && h === 12) h = 0
    return h * 60 + (m || 0)
}

export default {
    name: 'MoonAltitudeChart',
    props: {
        moonrise:    { type: String,  default: '' },
        moonset:     { type: String,  default: '' },
        transitAlt:  { type: Number,  default: null },
        currentTime: { type: Date,    default: null },
        title:       { type: String,  default: '' },
        subtitle:    { type: String,  default: '' },
        subtitle2:   { type: String,  default: '' },
    },
    watch: {
        transitAlt()  { this.redraw() },
        currentTime() { this.redraw() },
        moonrise()    { this.redraw() },
        moonset()     { this.redraw() },
    },
    mounted() {
        this.redraw()
    },
    methods: {
        redraw() {
            const canvas = this.$refs.canvas
            if (!canvas || this.transitAlt === null || !this.moonrise || !this.moonset) return

            const dpr  = window.devicePixelRatio || 1
            const cssW = canvas.parentElement ? canvas.parentElement.clientWidth : 600
            const cssH = parseInt(canvas.style.height) || canvas.height
            canvas.width  = cssW * dpr
            canvas.height = cssH * dpr
            canvas.style.height = cssH + 'px'
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const w = cssW
            const h = cssH

            const PAD_TOP    = 56
            const PAD_BOTTOM = 40
            const PAD_LEFT   = 40
            const PAD_RIGHT  = 16
            const plotW = w - PAD_LEFT - PAD_RIGHT
            const plotH = h - PAD_TOP - PAD_BOTTOM
            const plotBottom = PAD_TOP + plotH

            ctx.clearRect(0, 0, w, h)

            if (this.title) {
                ctx.font = 'bold 11px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.6)'
                ctx.fillText(this.title, PAD_LEFT, 16)
            }
            if (this.subtitle) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle, PAD_LEFT, 28)
            }
            if (this.subtitle2) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle2, PAD_LEFT, 40)
            }

            const moonriseMin = timeToMinutes(this.moonrise)
            const moonsetMin  = timeToMinutes(this.moonset)
            const transitMin  = (moonriseMin + moonsetMin) / 2

            const moonriseX = PAD_LEFT
            const moonsetX  = PAD_LEFT + plotW
            const transitX  = PAD_LEFT + ((transitMin - moonriseMin) / (moonsetMin - moonriseMin)) * plotW
            const transitY  = PAD_TOP
            const cpY       = 2 * transitY - plotBottom

            const toX = min => PAD_LEFT + ((min - moonriseMin) / (moonsetMin - moonriseMin)) * plotW
            const toY = alt => plotBottom - (alt / this.transitAlt) * plotH

            // Arc fill
            ctx.beginPath()
            ctx.moveTo(moonriseX, plotBottom)
            ctx.quadraticCurveTo(transitX, cpY, moonsetX, plotBottom)
            ctx.closePath()
            const dayGrad = ctx.createLinearGradient(0, PAD_TOP, 0, plotBottom)
            dayGrad.addColorStop(0, 'rgba(160, 180, 220, 0.20)')
            dayGrad.addColorStop(1, 'rgba(160, 180, 220, 0.03)')
            ctx.fillStyle = dayGrad
            ctx.fill()

            // Arc line
            ctx.beginPath()
            ctx.moveTo(moonriseX, plotBottom)
            ctx.quadraticCurveTo(transitX, cpY, moonsetX, plotBottom)
            ctx.strokeStyle = 'rgba(160, 180, 220, 0.85)'
            ctx.lineWidth = 2
            ctx.setLineDash([])
            ctx.stroke()

            // Baseline
            ctx.beginPath()
            ctx.moveTo(PAD_LEFT, plotBottom)
            ctx.lineTo(PAD_LEFT + plotW, plotBottom)
            ctx.strokeStyle = 'rgba(0,0,0,0.18)'
            ctx.lineWidth = 1
            ctx.setLineDash([4, 4])
            ctx.stroke()
            ctx.setLineDash([])

            // Y-axis: 0° label
            ctx.font = '10px system-ui, sans-serif'
            ctx.textAlign = 'right'
            ctx.fillStyle = 'rgba(0,0,0,0.35)'
            ctx.fillText('0°', PAD_LEFT - 8, plotBottom + 4)

            // Transit vertical + horizontal projections
            ctx.beginPath()
            ctx.moveTo(transitX, PAD_TOP)
            ctx.lineTo(transitX, plotBottom)
            ctx.strokeStyle = 'rgba(120, 140, 200, 0.25)'
            ctx.lineWidth = 1
            ctx.setLineDash([4, 4])
            ctx.stroke()

            ctx.beginPath()
            ctx.moveTo(PAD_LEFT, transitY)
            ctx.lineTo(transitX, transitY)
            ctx.stroke()
            ctx.setLineDash([])

            // Transit dot
            ctx.beginPath()
            ctx.arc(transitX, transitY, 4, 0, Math.PI * 2)
            ctx.fillStyle = '#fff'
            ctx.fill()
            ctx.strokeStyle = 'rgba(120, 140, 200, 0.85)'
            ctx.lineWidth = 2
            ctx.stroke()

            // Transit Y-axis label
            ctx.font = '10px system-ui, sans-serif'
            ctx.textAlign = 'right'
            ctx.fillStyle = 'rgba(120, 140, 200, 0.85)'
            ctx.fillText(Math.round(this.transitAlt) + '°', PAD_LEFT - 8, transitY + 4)

            // Moonrise / Moonset dots + x-axis labels
            for (const [mx, timeStr] of [
                [moonriseX, this.moonrise],
                [moonsetX,  this.moonset],
            ]) {
                ctx.beginPath()
                ctx.arc(mx, plotBottom, 4, 0, Math.PI * 2)
                ctx.fillStyle = '#fff'
                ctx.fill()
                ctx.strokeStyle = 'rgba(160, 180, 220, 0.85)'
                ctx.lineWidth = 2
                ctx.stroke()

                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = mx === moonriseX ? 'left' : 'right'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(timeStr.replace(/\s+(am|pm)$/i, '$1'), mx, plotBottom + 14)
            }

            // Transit x-axis label
            ctx.font = '10px system-ui, sans-serif'
            ctx.textAlign = 'center'
            ctx.fillStyle = 'rgba(120, 140, 200, 0.85)'
            ctx.fillText('Transit', transitX, plotBottom + 14)

            // Now marker — only when moon is above horizon
            if (this.currentTime instanceof Date) {
                const nowMin = this.currentTime.getHours() * 60 + this.currentTime.getMinutes()
                if (nowMin >= moonriseMin && nowMin <= moonsetMin) {
                    const t      = (nowMin - moonriseMin) / (moonsetMin - moonriseMin)
                    const nowAlt = this.transitAlt * 4 * t * (1 - t)
                    const nowX   = toX(nowMin)
                    const nowY   = toY(nowAlt)

                    // Horizontal projection to Y-axis
                    ctx.beginPath()
                    ctx.moveTo(PAD_LEFT, nowY)
                    ctx.lineTo(nowX, nowY)
                    ctx.strokeStyle = 'rgba(50, 160, 90, 0.25)'
                    ctx.lineWidth = 1
                    ctx.setLineDash([4, 4])
                    ctx.stroke()

                    // Vertical projection to x-axis
                    ctx.beginPath()
                    ctx.moveTo(nowX, nowY)
                    ctx.lineTo(nowX, plotBottom)
                    ctx.stroke()
                    ctx.setLineDash([])

                    // Now dot
                    ctx.beginPath()
                    ctx.arc(nowX, nowY, 4, 0, Math.PI * 2)
                    ctx.fillStyle = '#fff'
                    ctx.fill()
                    ctx.strokeStyle = 'rgba(50, 160, 90, 0.85)'
                    ctx.lineWidth = 2
                    ctx.stroke()

                    // Y-axis label — push down if too close to transit label
                    const transitLabelY = transitY + 4
                    let nowLabelY = nowY + 4
                    if (nowLabelY - transitLabelY < 14) nowLabelY = transitLabelY + 14
                    ctx.font = '10px system-ui, sans-serif'
                    ctx.textAlign = 'right'
                    ctx.fillStyle = 'rgba(50, 160, 90, 0.85)'
                    ctx.fillText(Math.round(nowAlt) + '°', PAD_LEFT - 8, nowLabelY)

                    // X-axis time label (second row)
                    const nowH  = this.currentTime.getHours()
                    const nowM  = String(this.currentTime.getMinutes()).padStart(2, '0')
                    const ampm  = nowH >= 12 ? 'pm' : 'am'
                    const h12   = nowH % 12 || 12
                    ctx.textAlign = 'center'
                    ctx.fillStyle = 'rgba(50, 160, 90, 0.85)'
                    ctx.fillText(`${h12}:${nowM}${ampm}`, nowX, plotBottom + 28)
                }
            }
        },
    },
}
</script>
